/**
 * Client-side auth utilities
 *
 * Handles token storage and authenticated fetch requests.
 * Automatically refreshes tokens when they expire.
 */

import { API_BASE } from './api';

const TOKEN_KEY = 'accessToken';
const SESSION_KEY = 'sessionId';
const DEVICE_KEY = 'deviceId';

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

// Callback for external listeners (e.g., Zustand store) to sync on token refresh
let onTokenRefreshed: ((token: string) => void) | null = null;

export function setOnTokenRefreshed(callback: ((token: string) => void) | null) {
  onTokenRefreshed = callback;
}

/**
 * Get stored access token
 */
export function getAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Get stored session ID
 */
export function getSessionId(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(SESSION_KEY);
}

/**
 * Set access token
 */
export function setAccessToken(token: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Set session ID
 */
export function setSessionId(sessionId: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(SESSION_KEY, sessionId);
}

/**
 * Get or create device ID
 */
export function getDeviceId(): string {
  if (typeof window === 'undefined') return '';

  let deviceId = localStorage.getItem(DEVICE_KEY);
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem(DEVICE_KEY, deviceId);
  }
  return deviceId;
}

/**
 * Clear auth data (logout)
 */
export function clearAuth(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(SESSION_KEY);
}

/**
 * Check if user is logged in (has token)
 */
export function isLoggedIn(): boolean {
  return !!getAccessToken();
}

/**
 * Attempt to refresh the access token
 */
async function refreshAccessToken(): Promise<boolean> {
  const sessionId = getSessionId();
  const deviceId = getDeviceId();

  if (!sessionId) {
    return false;
  }

  try {
    const response = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, deviceId }),
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    if (data.accessToken) {
      setAccessToken(data.accessToken);
      onTokenRefreshed?.(data.accessToken);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Authenticated fetch - automatically adds auth header and handles token refresh
 */
export async function authFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  // resolve relative /api/* paths against the configured backend base
  const fullUrl = url.startsWith("http") ? url : `${API_BASE}${url}`;
  const token = getAccessToken();

  const headers = new Headers(options.headers);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(fullUrl, {
    ...options,
    headers,
  });

  // If we get a 401, try to refresh the token and retry
  if (response.status === 401 && getSessionId()) {
    // Prevent multiple simultaneous refresh attempts
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = refreshAccessToken().finally(() => {
        isRefreshing = false;
      });
    }

    const refreshed = await (refreshPromise ?? Promise.resolve(false));

    if (refreshed) {
      // Retry the original request with the new token
      const newToken = getAccessToken();
      const retryHeaders = new Headers(options.headers);
      if (newToken) {
        retryHeaders.set('Authorization', `Bearer ${newToken}`);
      }
      return fetch(fullUrl, {
        ...options,
        headers: retryHeaders,
      });
    } else {
      // Refresh failed, clear auth
      clearAuth();
    }
  }

  return response;
}
